import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text } from '@/components/ui/text/Text';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { useCreateMedicalLeave } from '@/hooks/absence/useCreateMedicalLeave';
import { HALF_DAY_WORKDAY_TYPES } from '@/api/domains/absence/absence.types';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { colors } from '@/assets/styles/colors';


const HalfDayCountHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  return (
    <Text variant="small-regular" color={colors.important.main}>
      {translate('medicalLeave.form.halfDayCountHint')}
    </Text>
  );
};

const BlockedEmployeeHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="relative flex items-center gap-1 mt-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div ref={anchorRef} className="flex items-center gap-1 cursor-help">
        <Text variant="small-regular" color={colors.grays.neutral66}>
          {translate('medicalLeave.form.blockedEmployeeHint')}
        </Text>
      </div>
      {showTooltip && (
        <Tooltip
          body={translate('medicalLeave.form.blockedEmployeeTooltip')}
          anchorRef={anchorRef}
          strategy="auto"
          placement="top"
          maxWidth={300}
        />
      )}
    </div>
  );
};

export const CreateMedicalLeaveForm: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  const {
    form,
    isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    employeeBlockedIds,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    leaveTypeOptions,
    isLoadingLeaveTypes,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
  } = useCreateMedicalLeave();

  const hasBlockedEmployees = employeeBlockedIds.size > 0;

  const startDateValue = form.watch('startDate');
  const workdayTypeValue = form.watch('workdayType');
  const dayCountValue = Number(form.watch('dayCount'));
  const showHalfDayCountHint =
    HALF_DAY_WORKDAY_TYPES.has(workdayTypeValue) && dayCountValue >= 1;

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="create-medical-leave-form">
      <SidePanelFormLayout.Fields>
        <div className="flex flex-col gap-1">
          <Selector
            label={translate('medicalLeave.form.employee')}
            placeholder={translate('medicalLeave.form.employeePlaceholder')}
            options={employeeOptions}
            value={form.watch('employeeTargetId') ? String(form.watch('employeeTargetId')) : undefined}
            onChange={handleEmployeeChange}
            searchable
            onSearchChange={handleEmployeeSearch}
            hasNextPage={employeeHasMore}
            onLoadMore={loadMoreEmployees}
            isLoading={isLoadingEmployees}
            isLoadingMore={isLoadingMoreEmployees}
            disabled={isSubmitting}
            color={form.errors.employeeTargetId && isSubmitted ? 'error' : 'default'}
            supportingText={
              form.errors.employeeTargetId && isSubmitted
                ? translate(form.errors.employeeTargetId.message as string)
                : undefined
            }
            required
            data-testid="employee-selector"
          />
          {hasBlockedEmployees && <BlockedEmployeeHint />}
        </div>

        <Selector
          label={translate('medicalLeave.form.leaveType')}
          placeholder={translate('medicalLeave.form.leaveTypePlaceholder')}
          options={leaveTypeOptions}
          value={form.watch('leaveTypeId') ? String(form.watch('leaveTypeId')) : undefined}
          onChange={(value) => {
            const numericValue = normalizeNumericSelectValue(value);
            if (numericValue !== undefined) {
              form.setValue('leaveTypeId', numericValue, { shouldDirty: true });
            }
          }}
          disabled={isSubmitting || isLoadingLeaveTypes}
          color={form.errors.leaveTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.leaveTypeId && isSubmitted
              ? translate(form.errors.leaveTypeId.message as string)
              : undefined
          }
          required
          data-testid="leave-type-selector"
        />

        <LabelInput
          label={translate('medicalLeave.form.folio')}
          registration={form.register('folio')}
          disabled={isSubmitting}
          maxLength={50}
          color={form.errors.folio && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.folio && isSubmitted
              ? translate(form.errors.folio.message as string)
              : undefined
          }
          required
          data-testid="folio-input"
        />

        <Selector
          label={translate('medicalLeave.form.workdayType')}
          placeholder={translate('medicalLeave.form.workdayTypePlaceholder')}
          options={workdayTypeOptions}
          value={form.watch('workdayType') || undefined}
          onChange={(value) => {
            const codeValue = Array.isArray(value) ? value[0] : value;
            if (codeValue) {
              form.setValue('workdayType', codeValue, { shouldDirty: true });
            }
          }}
          disabled={isSubmitting || isLoadingWorkdayTypes}
          color={form.errors.workdayType && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.workdayType && isSubmitted
              ? translate(form.errors.workdayType.message as string)
              : undefined
          }
          required
          data-testid="workday-type-selector"
        />

        <div className="flex flex-col gap-1">
          <LabelInput
            label={translate('medicalLeave.form.dayCount')}
            type="number"
            registration={form.register('dayCount')}
            disabled={isSubmitting}
            color={form.errors.dayCount && isSubmitted ? 'error' : undefined}
            supportingText={
              form.errors.dayCount && isSubmitted
                ? translate(form.errors.dayCount.message as string)
                : undefined
            }
            required
            data-testid="day-count-input"
          />
          {showHalfDayCountHint && <HalfDayCountHint />}
        </div>

        <DatePicker
          label={translate('medicalLeave.form.startDate')}
          value={form.watch('startDate') || undefined}
          onChange={(date) => form.setValue('startDate', date, { shouldDirty: true })}
          disabled={isSubmitting}
          iconColor={colors.feedback.success300}
          color={form.errors.startDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.startDate && isSubmitted
              ? translate(form.errors.startDate.message as string)
              : undefined
          }
          required
          data-testid="start-date-picker"
        />

        <DatePicker
          label={translate('medicalLeave.form.endDate')}
          value={form.watch('endDate') || undefined}
          disabled
          minDate={startDateValue || undefined}
          iconColor={colors.feedback.error300}
          color={form.errors.endDate && isSubmitted ? 'error' : undefined}
          supportingText={
            form.errors.endDate && isSubmitted
              ? translate(form.errors.endDate.message as string)
              : undefined
          }
          required
          data-testid="end-date-picker"
        />

        <DatePicker
          label={translate('medicalLeave.form.returnDate')}
          value={form.watch('returnDate') || undefined}
          disabled
          data-testid="return-date-picker"
        />

      </SidePanelFormLayout.Fields>

      <SidePanelFormLayout.Footer>
        <PermissionGuard action="create">
          <PrimaryButton
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
            data-testid="submit-medical-leave-button"
          >
            {translate('medicalLeave.form.submit')}
          </PrimaryButton>
        </PermissionGuard>
      </SidePanelFormLayout.Footer>
    </SidePanelFormLayout>
  );
};

export default CreateMedicalLeaveForm;
